<script setup lang="ts">
import { computed, inject, ref, watch } from "vue";
import { storeToRefs } from "pinia";

import PanelMenu from "primevue/panelmenu";
import type { PanelMenuExpandedKeys } from "primevue/panelmenu";

import SidebarLabelMenuItem from "@/components/navigation/SidebarLabelMenuItem.vue";
import SidebarRouterMenuItem from "@/components/navigation/SidebarRouterMenuItem.vue";

import { useProductsStore } from "@/stores/Products";

import { useMenuPermissions } from "@/composables/menu-permissions";
import { useVersionInfo } from "@/services/version-service";

import type { LabelService } from "@/composables/label-service";
import type { NavItem } from "@tmi/types/navigation";
import type { Product } from "@tmi/types/entities";

import { labelServiceSymbol } from "@/plugins/LabelServicePlugin";
import routeManager from "@/router/route-manager";
import { isSidebarLinkActive } from "@/composables/sidebar-nav-active";
import { useRoute } from "vue-router";

/** Menu entries use `useMenuPermissions()` so each link respects the same flags as before (alerts, learning, tools, etc.). */
const labelService = inject<LabelService>(labelServiceSymbol) as LabelService;
const { currentVersion } = useVersionInfo();
const productsStore = useProductsStore();
const { availableProducts, subscribedProducts } = storeToRefs(productsStore);
const route = useRoute();
const {
  showBonusReportOption,
  showCoursesMenuOption,
  showEtfListingMenuOption,
  showMajorIndexBreadthMenuOption,
  showNyseBpiMenuOption,
  showOptionsCalculatorMenuOption,
  showPortfolioBuilderMenuOption,
  showPositionSizeCalculatorMenuOption,
  showRelativeStrengthMatrixMenuOption,
  showSectorAlertsMenuOption,
  showSectorBpiMenuOption,
  showSectorsMenuOption,
  showSectorTreeMenuOption,
  showSpreadCalculatorMenuOption,
  showToolsMenuItem,
  showTradeAlertsMenuOption,
  showWebinarsMenuOption,
  showArticlesOption,
} = useMenuPermissions();

const productsNavItems = computed<NavItem[]>(() => {
  const products: NavItem[] = [];

  products.push({
    id: "sector-edge-menuitem",
    key: "sector-edge-menuitem",
    label: labelService.label("lblSectorEdge"),
    route: routeManager.sectorEdgePath(),
    visible: true,
    image: "/img/logos/sectoredge-logo-32-32.png",
  });

  subscribedProducts.value.forEach((product: Product) => {
    products.push({
      id: `product-${product.slug}-menuitem`,
      key: `product-${product.slug}-menuitem`,
      label: product.name,
      route: `/products/${product.slug}`,
      image: product.favicon,
      visible: true,
    });
  });

  availableProducts.value.forEach((product: Product) => {
    products.push({
      id: `product-${product.slug}-menuitem`,
      key: `product-${product.slug}-menuitem`,
      label: product.name,
      route: `/products/${product.slug}`,
      icon: "pi-lock",
      visible: true,
    });
  });

  return products;
});

const dashboardMenuKey = "dashboard-menu";

const dashboardItem = computed<NavItem>(() => ({
  label: labelService.label("lblDashboard"),
  id: dashboardMenuKey,
  key: dashboardMenuKey,
  visible: true,
  route: routeManager.homePath(),
  icon: "pi-gauge",
  class: "text-base",
  isRootItem: true,
}));

/** Alerts (permission-gated). Order: Trade Alerts, Sector Alerts. */
const alertNavItems = computed<NavItem[]>(() => {
  const items: NavItem[] = [];
  if (showTradeAlertsMenuOption.value) {
    items.push({
      id: "trade-alerts-menuitem",
      key: "trade-alerts-menuitem",
      label: labelService.label("lblTradeAlerts"),
      route: routeManager.tradeAlertsPath(),
      visible: true,
      icon: "pi-chart-line",
      isRootItem: true,
    });
  }
  if (showSectorAlertsMenuOption.value) {
    items.push({
      id: "sector-alerts-menuitem",
      key: "sector-alerts-menuitem",
      label: labelService.label("lblSectorAlerts"),
      route: routeManager.sectorAlertsPath(),
      visible: true,
      icon: "pi-bolt",
      isRootItem: true,
    });
  }
  return items;
});

/** Learning / content (permission-gated). Order: Articles, Webinars, Bonus Reports, Courses. */
const contentNavItems = computed<NavItem[]>(() => {
  const items: NavItem[] = [];
  if (showArticlesOption.value) {
    items.push({
      id: "articles-menuitem",
      key: "articles-menuitem",
      label: labelService.label("lblArticles"),
      route: routeManager.articleSearchPath(),
      visible: true,
      icon: "pi-folder",
      isRootItem: true,
    });
  }
  if (showWebinarsMenuOption.value) {
    items.push({
      id: "webinars-menuitem",
      key: "webinars-menuitem",
      label: labelService.label("lblWebinars"),
      route: routeManager.webinarSearchPath(),
      visible: true,
      icon: "pi-video",
      isRootItem: true,
    });
  }
  if (showBonusReportOption.value) {
    items.push({
      id: "bonus-reports-menuitem",
      key: "bonus-reports-menuitem",
      label: labelService.label("lblBonusReports"),
      route: routeManager.bonusReportsSearchPath(),
      visible: true,
      icon: "pi-file-pdf",
      isRootItem: true,
    });
  }
  if (showCoursesMenuOption.value) {
    items.push({
      id: "courses-menuitem",
      key: "courses-menuitem",
      label: labelService.label("lblCourses"),
      route: routeManager.coursesPath(),
      visible: true,
      icon: "pi-graduation-cap",
      isRootItem: true,
    });
  }
  return items;
});

/** Services panel: wraps all product links (Sector Edge + subscribed + available). */
const servicesMenuItem = computed<NavItem | null>(() => {
  const products = productsNavItems.value.filter((p) => p.visible);
  if (products.length === 0) return null;
  return {
    label: labelService.label("lblServices"),
    id: "services-menu",
    key: "services-menu",
    visible: true,
    icon: "pi-star",
    isRootItem: true,
    items: products,
  };
});

/** Alerts panel: top-level accordion for Trade Alerts + Sector Alerts. */
const alertsMenuItem = computed<NavItem | null>(() => {
  const alerts = alertNavItems.value;
  if (alerts.length === 0) return null;
  return {
    label: labelService.label("lblMarketAlerts"),
    id: "alerts-menu",
    key: "alerts-menu",
    visible: true,
    icon: "pi-bolt",
    isRootItem: true,
    items: alerts,
  };
});

/** Content panel: top-level accordion for Articles, Webinars, Bonus Reports, Courses. */
const contentMenuItem = computed<NavItem | null>(() => {
  const content = contentNavItems.value;
  if (content.length === 0) return null;
  return {
    label: labelService.label("lblContent"),
    id: "content-menu",
    key: "content-menu",
    visible: true,
    icon: "pi-book",
    isRootItem: true,
    items: content,
  };
});

const toolsMenuItem = computed<NavItem | null>(() => {
  if (!showToolsMenuItem.value) {
    return null;
  }
  return {
    label: labelService.label("lblTools"),
    id: "tools-menu",
    key: "tools-menu",
    visible: true,
    icon: "pi-hammer",
    isRootItem: true,
    items: [
      {
        id: "Sectors-menuitem",
        key: "Sectors-menuitem",
        label: labelService.label("lblSectors"),
        route: routeManager.sectorListingPath(),
        visible: showSectorsMenuOption.value,
        icon: "pi-list",
      },
      {
        id: "SectorTree-menuitem",
        key: "SectorTree-menuitem",
        label: labelService.label("lblSectorTree"),
        route: routeManager.sectorTreePath(),
        visible: showSectorTreeMenuOption.value,
        icon: "pi-list",
      },
      {
        id: "EtfListing-menuitem",
        key: "EtfListing-menuitem",
        label: labelService.label("lblEtfListing"),
        route: routeManager.etfListingPath(),
        visible: showEtfListingMenuOption.value,
        icon: "pi-list",
      },
      {
        id: "RsMatrix-menuitem",
        key: "RsMatrix-menuitem",
        label: labelService.label("lblRsMatrix"),
        route: routeManager.relativeStrengthMatrixPath(),
        visible: showRelativeStrengthMatrixMenuOption.value,
        icon: "pi-chart-bar",
      },
      {
        id: "sectorbpis-menuitem",
        key: "sectorbpis-menuitem",
        label: labelService.label("lblSectorBpis"),
        route: routeManager.sectorBullishPercentIndicesPath(),
        visible: showSectorBpiMenuOption.value,
        icon: "pi-chart-line",
      },
      {
        id: "MajorIndexBreadth-menuitem",
        key: "MajorIndexBreadth-menuitem",
        label: labelService.label("lblMajorIndexBreadth"),
        route: routeManager.majorIndexBreadthPath(),
        visible: showMajorIndexBreadthMenuOption.value,
        icon: "pi-chart-line",
      },
      {
        id: "NyseBpi-menuitem",
        key: "NyseBpi-menuitem",
        label: labelService.label("lblNyseBpi"),
        route: routeManager.nyseBullishPercentIndexPath(),
        visible: showNyseBpiMenuOption.value,
        icon: "pi-chart-line",
      },
      {
        id: "OptionsCalculator-menuitem",
        key: "OptionsCalculator-menuitem",
        label: labelService.label("lblOptionsCalculator"),
        route: routeManager.optionsCalculatorPath(),
        visible: showOptionsCalculatorMenuOption.value,
        icon: "pi-calculator",
      },
      {
        id: "PortfolioBuilder-menuitem",
        key: "PortfolioBuilder-menuitem",
        label: labelService.label("lblPortfolioBuilder"),
        route: routeManager.portfolioBuilderPath(),
        visible: showPortfolioBuilderMenuOption.value,
        icon: "pi-briefcase",
      },
      {
        id: "PositionSizeCalculator-menuitem",
        key: "PositionSizeCalculator-menuitem",
        label: labelService.label("lblPositionSizeCalculator"),
        route: routeManager.positionSizeCalculatorPath(),
        visible: showPositionSizeCalculatorMenuOption.value,
        icon: "pi-calculator",
      },
      {
        id: "SpreadCalculators-menuitem",
        key: "SpreadCalculators-menuitem",
        label: labelService.label("lblSpreadCalculators"),
        route: routeManager.spreadCalculatorPath(),
        visible: showSpreadCalculatorMenuOption.value,
        icon: "pi-calculator",
      },
    ],
  };
});

const panelMenuItems = computed<NavItem[]>(() => {
  const items: NavItem[] = [];
  if (servicesMenuItem.value) items.push(servicesMenuItem.value);
  if (alertsMenuItem.value) items.push(alertsMenuItem.value);
  if (contentMenuItem.value) items.push(contentMenuItem.value);
  const tools = toolsMenuItem.value;
  if (tools) items.push(tools);
  return items;
});

const expandedMenuItemKeys = ref<PanelMenuExpandedKeys>({});

const menuPtStyle = {
  header:
    "cursor-pointer select-none touch-manipulation transition-colors duration-150 hover:bg-primary/10 dark:hover:bg-primary/15",
  headerContent: "cursor-pointer select-none min-w-0",
  submenu: "overflow-hidden",
  submenulabel: "border-solid border-t-[0.5px] border-t-base-content/[0.07] mx-2 mt-2 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-base-content/60",
  itemcontent: "m-0 pl-2 cursor-pointer select-none touch-manipulation",
  item: { class: "sidebar-panel-menu-item cursor-pointer select-none" },
};

function panelContainsRoute(panel: NavItem, path: string): boolean {
  return !!panel.items?.some((child) => {
    if (child.visible !== false && child.route && isSidebarLinkActive(path, child.route)) return true;
    if (child.items) return panelContainsRoute(child, path);
    return false;
  });
}

function openInitialMenuPanels() {
  const path = route.path;
  const next: PanelMenuExpandedKeys = {};
  for (const panel of panelMenuItems.value) {
    if (panel.key && panelContainsRoute(panel, path)) {
      next[panel.key] = true;
    }
  }
  // Services panel is open by default; Alerts and Content start closed
  if (servicesMenuItem.value?.key) next[servicesMenuItem.value.key] = true;
  expandedMenuItemKeys.value = next;
}

const didInitExpandedPanels = ref(false);

watch(
  () => panelMenuItems.value.length,
  () => {
    if (!didInitExpandedPanels.value && panelMenuItems.value.length > 0) {
      openInitialMenuPanels();
      didInitExpandedPanels.value = true;
    }
  },
  { immediate: true },
);

watch(
  () => route.path,
  () => {
    if (!didInitExpandedPanels.value) {
      return;
    }
    const path = route.path;
    const next = { ...expandedMenuItemKeys.value };
    for (const panel of panelMenuItems.value) {
      if (panel.key && panelContainsRoute(panel, path)) {
        next[panel.key] = true;
        // Also open any matching sub-panels
        for (const sub of panel.items ?? []) {
          if (sub.key && panelContainsRoute(sub, path)) {
            next[sub.key] = true;
          }
        }
      }
    }
    expandedMenuItemKeys.value = next;
  },
);
</script>

<template>
  <div class="flex flex-col h-full min-h-0 w-full min-w-0 max-w-full">
    <router-link
      :to="routeManager.homePath()"
      class="shrink-0 tmi-logo w-32 min-w-32 max-w-32 p-2 h-9 lg:hidden block"
    />

    <SidebarRouterMenuItem v-if="dashboardItem.visible" :item="dashboardItem" />

    <!-- eslint-disable vue/no-v-model-argument -- PrimeVue PanelMenu uses expandedKeys v-model -->
    <PanelMenu
      v-if="panelMenuItems.length > 0"
      :model="panelMenuItems"
      :multiple="true"
      unstyled
      :pt="menuPtStyle"
      v-model:expandedKeys="expandedMenuItemKeys"
    >
      <template #item="{ item }">
        <SidebarRouterMenuItem v-if="item.visible && item.route" :item="item" nested />
        <SidebarLabelMenuItem v-else-if="item.visible" :item="item" />
      </template>
    </PanelMenu>
    <!-- eslint-enable vue/no-v-model-argument -->

    <div class="mt-auto border-solid border-t-[0.5px] border-t-base-content/[0.07] p-3 text-xs text-base-content/50 text-center">
      <p class="text-xs mb-0">v{{ currentVersion }} © True Market Insiders {{ new Date().getFullYear() }}.</p>
      <p class="text-xs mb-0">All rights reserved.</p>
    </div>

  </div>
</template>
